import { OrderState, ReturnPaymentState, ReturnShipmentState } from '~/types/GraphQL';
import type { Maybe, PaymentState, RawCustomField, ShipmentState } from '~/types/GraphQL';

const calculateAge = (birthday: Date) => {
  const currentDate = new Date();
  const diff = currentDate.valueOf() - birthday.valueOf();
  // This is the difference in milliseconds
  const currentAge = Math.floor(diff / 31557600000);
  // Divide by 1000*60*60*24*365.25
  return currentAge;
};

const findAttr = (attributes: RawCustomField[], name: string) => attributes.find(attr => attr.name === name)?.value;

const getLocalizedDate = (date: Date, locale: string) => {
  const createdAt = new Date(date);

  return createdAt.toLocaleDateString(locale, {
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
};

function getReturnStatus(shipmentState: ReturnShipmentState, paymentState: ReturnPaymentState) {
  if (
    shipmentState === ReturnShipmentState.Returned
    || (shipmentState === ReturnShipmentState.BackInStock && paymentState === ReturnPaymentState.Initial)
  ) {
    return 'In process';
  }
  if (shipmentState === ReturnShipmentState.BackInStock && paymentState === ReturnPaymentState.Refunded) {
    return 'finalized';
  }
  if (shipmentState === ReturnShipmentState.BackInStock && paymentState === ReturnPaymentState.NotRefunded) {
    return 'Clarification case';
  }

  return '';
}

function getReturnStatusIconKey(shipmentState: ReturnShipmentState, paymentState: ReturnPaymentState) {
  if (
    shipmentState === ReturnShipmentState.Returned
    || (shipmentState === ReturnShipmentState.BackInStock && paymentState === ReturnPaymentState.Initial)
  ) {
    return ['orderProcess', 'fill-warning-400'];// In process
  }
  if (shipmentState === ReturnShipmentState.BackInStock && paymentState === ReturnPaymentState.Refunded) {
    return ['orderDelivered', 'fill-positive-600'];// finalized
  }
  if (shipmentState === ReturnShipmentState.BackInStock && paymentState === ReturnPaymentState.NotRefunded) {
    return ['warning', 'fill-neutral-300'];// 'Clarification case';
  }

  return ['', ''];
}

function getOrderStatus(paymentMethod: Maybe<string | undefined>, orderStatus: OrderState | undefined, shipmentStatus: Maybe<ReturnShipmentState | ShipmentState | undefined>, paymentStatus: Maybe<PaymentState | undefined>) {
  switch (paymentMethod) {
    case 'vorkasse': {
      if (orderStatus === OrderState.Open) {
        return 'Waiting for payment';
      }
      if (orderStatus === OrderState.Confirmed && paymentStatus === 'Pending') {
        return 'Waiting for payment';
      }
      if (orderStatus === OrderState.Confirmed && paymentStatus === 'Paid') {
        return 'In process';
      }
      if (orderStatus === OrderState.Complete) {
        return 'shipped';
      }
      if (orderStatus === OrderState.Cancelled) {
        return 'cancelled';
      }
      return '';
    }
    case 'kik_filiale': {
      if (orderStatus === OrderState.Open) {
        return 'In process';
      }
      if (orderStatus === OrderState.Confirmed && shipmentStatus === 'Pending' && paymentStatus === 'Pending') {
        return 'On the way to the store';
      }
      if (orderStatus === OrderState.Confirmed && shipmentStatus === 'Shipped' && paymentStatus === 'Pending') {
        return 'Ready for pickup in the store';
      }
      if (orderStatus === OrderState.Confirmed && paymentStatus === 'Pending') {
        return 'In process';
      }
      if (orderStatus === OrderState.Complete) {
        return 'Picked up at the store';
      }
      if (orderStatus === OrderState.Cancelled) {
        return 'cancelled';
      }
      return '';
    }
    default: {
      if (orderStatus === OrderState.Open) {
        return 'In process';
      }
      if (orderStatus === OrderState.Confirmed) {
        return 'In process';
      }
      if (orderStatus === OrderState.Complete) {
        return 'shipped';
      }
      if (orderStatus === OrderState.Cancelled) {
        return 'cancelled';
      }
      return '';
    }
  }
}

function getOrderStatusIconKey(paymentMethod: Maybe<string | undefined>, orderStatus: OrderState | undefined, shipmentStatus: Maybe<ReturnShipmentState | ShipmentState | undefined>, paymentStatus: Maybe<PaymentState | undefined>) {
  switch (paymentMethod) {
    case 'vorkasse': {
      if (orderStatus === OrderState.Open) {
        return ['orderProcess', 'fill-warning-400'];// Waiting for payment
      }
      if (orderStatus === OrderState.Confirmed && paymentStatus === 'Pending') {
        return ['orderProcess', 'fill-warning-400'];// Waiting for payment
      }
      if (orderStatus === OrderState.Confirmed && paymentStatus === 'Paid') {
        return ['orderProcess', 'fill-warning-400'];// In process
      }
      if (orderStatus === OrderState.Complete) {
        return ['orderMove', 'fill-warning-400'];// shipped
      }
      if (orderStatus === OrderState.Cancelled) {
        return ['orderCanceled', 'fill-neutral-300'];// cancelled
      }
      return ['', ''];
    }
    case 'kik_filiale': {
      if (orderStatus === OrderState.Open) {
        return ['orderProcess', 'fill-warning-400'];// In process
      }
      if (orderStatus === OrderState.Confirmed && shipmentStatus === 'Pending' && paymentStatus === 'Pending') {
        return ['orderMove', 'fill-warning-400'];// On the way to the store
      }
      if (orderStatus === OrderState.Confirmed && shipmentStatus === 'Shipped' && paymentStatus === 'Pending') {
        return ['orderReady', 'fill-warning-400'];// Ready for pickup in the store
      }
      if (orderStatus === OrderState.Confirmed && paymentStatus === 'Pending') {
        return ['orderProcess', 'fill-warning-400'];// In process
      }
      if (orderStatus === OrderState.Complete) {
        return ['orderDelivered', 'fill-positive-600'];// Picked up at the store
      }
      if (orderStatus === OrderState.Cancelled) {
        return ['orderCanceled', 'fill-neutral-300'];// cancelled
      }
      return ['', ''];
    }
    default: {
      if (orderStatus === OrderState.Open) {
        return ['orderProcess', 'fill-warning-400'];// In process
      }
      if (orderStatus === OrderState.Confirmed) {
        return ['orderProcess', 'fill-warning-400'];// In process
      }
      if (orderStatus === OrderState.Complete) {
        return ['orderMove', 'fill-warning-400'];// shipped
      }
      if (orderStatus === OrderState.Cancelled) {
        return ['orderCanceled', 'fill-neutral-300'];// cancelled
      }
      return ['', ''];
    }
  }
}

export {
  getOrderStatusIconKey,
  getOrderStatus,
  getReturnStatus,
  getReturnStatusIconKey,
  getLocalizedDate,
  findAttr,
  calculateAge,
};
